import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { htmlToText } from '../utils/htmlText';
import { recordHref, resolveTab, buildTabSearch } from '../utils/recordLinks';
import { RecordLink } from '../components/RecordLink';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Building2, Mail, Phone, MapPin, Calendar, Edit,
  Package, Ticket, FileText, Activity, DollarSign, Users, Plus, Trash2, Save, X,
  Lock, Key, Eye, EyeOff, Copy, Globe, Server, Database, Shield, Search, Loader2, ExternalLink, Images,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { compareInvoices } from '../utils/sortHelpers';
import ImageSearchDialog from '../components/ImageSearchDialog';
import { toast } from 'sonner';

// ── Portal Access Manager Component ──
const PortalAccessManager = ({ customerId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPortalAccess(); }, [customerId]);

  const loadPortalAccess = async () => {
    try {
      const r = await api.get(`/portal-access/customer/${customerId}`);
      setContacts(r.data.contacts || []);
    } catch { /* portal access may not be configured */ }
    setLoading(false);
  };

  const handleEnable = async (email, name) => {
    try {
      await api.post('/portal-access/enable', { email, customer_id: customerId, name, send_invite: true });
      toast.success(`Portal access enabled for ${email}. Invite email sent.`);
      loadPortalAccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDisable = async (email) => {
    if (!window.confirm(`Disable portal access for ${email}?`)) return;
    try {
      await api.post('/portal-access/disable', { email });
      toast.success(`Portal access disabled for ${email}`);
      loadPortalAccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <p className="text-gray-500 text-sm py-4">Loading...</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Portal Access</CardTitle>
        <CardDescription>Manage which customer contacts can log in to the support portal.</CardDescription>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <p className="text-gray-500 text-sm">No email addresses registered for this customer. Add contacts in the Edit dialog.</p>
        ) : (
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">{c.name || c.email}</p>
                  <p className="text-xs text-gray-500">{c.email} · <span className="capitalize">{c.source}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  {c.portal_enabled ? (
                    <>
                      <Badge variant="custom" className="bg-green-100 text-green-700">Active</Badge>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleDisable(c.email)}>Disable</Button>
                    </>
                  ) : (
                    <>
                      <Badge variant="custom" className="bg-gray-100 text-gray-600">No Access</Badge>
                      <Button size="sm" className="h-7 text-xs" onClick={() => handleEnable(c.email, c.name)}>Enable & Invite</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
import { format } from 'date-fns';
import CustomerCredits from '../components/CustomerCredits';
import { formatInCST } from '../utils/timezone';
import { buildReturnParam } from '../utils/returnNav';

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const CUSTOMER_TABS = ['overview', 'tickets', 'assets', 'invoices', 'credits', 'contacts', 'portal', 'secure-notes', 'credentials'];
  const activeTab = resolveTab(searchParams.get('tab'), CUSTOMER_TABS, 'overview');
  const handleTabChange = (value) => setSearchParams(buildTabSearch(searchParams, value, 'overview'));
  const [customer, setCustomer] = useState(null);
  const [assets, setAssets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invSort, setInvSort] = useState({ field: 'issue_date', order: 'desc' });
  const toggleInvSort = (field) => setInvSort(prev => prev.field === field ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'asc' });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageSearchOpen, setImageSearchOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [editForm, setEditForm] = useState({
    company_name: '', email: '', phone: '', contact_name: '',
    status: 'active', address: '', city: '', state: '',
    zip_code: '', notes: '', domain: ''
  });
  
  // Contact management state
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', title: '' });

  // Secure vault state
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultPassword, setVaultPassword] = useState('');
  const [vaultVerifying, setVaultVerifying] = useState(false);
  const [vaultNeedsSetup, setVaultNeedsSetup] = useState(false);
  const [vaultSetupPassword, setVaultSetupPassword] = useState('');
  const [vaultSetupConfirm, setVaultSetupConfirm] = useState('');
  const [secureNotes, setSecureNotes] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [noteForm, setNoteForm] = useState({ title: '', content: '' });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [credForm, setCredForm] = useState({ title: '', username: '', password: '', url: '', notes: '', credential_type: 'generic' });
  const [editingCredId, setEditingCredId] = useState(null);
  const [showCredForm, setShowCredForm] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [revealPassword, setRevealPassword] = useState('');
  const [revealingCredId, setRevealingCredId] = useState(null);

  useEffect(() => {
    loadCustomerDetails();
  }, [customerId]);

  // Proactively check vault status on load for SSO users
  useEffect(() => {
    if (customerId && !vaultUnlocked) {
      api.get(`/customers/${customerId}/vault/status`).then(res => {
        const { has_vault_password, has_login_password, needs_setup } = res.data;
        if (needs_setup || (!has_vault_password && !has_login_password)) {
          setVaultNeedsSetup(true);
        }
      }).catch(() => {});
    }
  }, [customerId, vaultUnlocked]);

  const handleEnrichFromWeb = async () => {
    if (!customer) return;
    setEnriching(true);
    try {
      const res = await api.post('/enrich/lookup', {
        company_name: customer.company_name,
        domain: customer.domain || '',
        email: customer.email || '',
      });
      setEnrichResult(res.data);
    } catch (err) { toast.error('Lookup failed'); }
    setEnriching(false);
  };

  const handleApplyEnrichment = async (fields) => {
    try {
      await api.post(`/enrich/apply/${customerId}`, fields);
      toast.success('Customer info updated');
      setEnrichResult(null);
      loadCustomerDetails();
    } catch (err) { toast.error('Failed to apply'); }
  };

  const loadCustomerDetails = async () => {
    setLoading(true);
    try {
      const [customerRes, assetsRes, ticketsRes, invoicesRes] = await Promise.all([
        api.get(`/customers/${customerId}`),
        api.get(`/assets?customer_id=${customerId}&limit=200`).catch(() => ({ data: [] })),
        api.get(`/tickets?customer_id=${customerId}&limit=200`).catch(() => ({ data: [] })),
        api.get(`/invoices?customer_id=${customerId}&limit=200`).catch(() => ({ data: [] }))
      ]);
      
      setCustomer(customerRes.data);
      setAssets(assetsRes.data);
      const ticketsData = ticketsRes.data;
      setTickets(Array.isArray(ticketsData) ? ticketsData : ticketsData.tickets || []);
      const invoicesData = invoicesRes.data;
      setInvoices(Array.isArray(invoicesData) ? invoicesData : invoicesData.invoices || []);
    } catch (error) {
      toast.error('Failed to load customer details');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading || !customer) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src="/cipherone-logo.png" alt="Loading" className="h-12 w-12 logo-spin" />
      </div>
    );
  }

  const handleAddContact = async () => {
    if (!contactForm.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    try {
      await api.post(`/customers/${customerId}/contacts`, contactForm);
      toast.success('Contact added');
      setAddContactOpen(false);
      setContactForm({ name: '', email: '', phone: '', title: '' });
      loadCustomerDetails();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleUpdateContact = async () => {
    if (!contactForm.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    try {
      await api.put(`/customers/${customerId}/contacts/${editingContact.id}`, contactForm);
      toast.success('Contact updated');
      setEditingContact(null);
      setContactForm({ name: '', email: '', phone: '', title: '' });
      loadCustomerDetails();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/customers/${customerId}/contacts/${contactId}`);
      toast.success('Contact removed');
      loadCustomerDetails();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  // ============ VAULT FUNCTIONS ============
  const handleUnlockVault = async () => {
    setVaultVerifying(true);
    try {
      await api.post(`/customers/${customerId}/vault/verify`, { password: vaultPassword });
      setVaultUnlocked(true);
      setVaultPassword('');
      setVaultNeedsSetup(false);
      loadSecureNotes();
      loadCredentials();
      toast.success('Vault unlocked');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 403 && detail.includes('No vault password set')) {
        setVaultNeedsSetup(true);
        toast.info('Please set a vault password to continue');
      } else {
        toast.error(detail || 'Verification failed');
      }
    } finally {
      setVaultVerifying(false);
    }
  };

  const handleSetVaultPassword = async () => {
    if (!vaultSetupPassword || vaultSetupPassword.length < 4) {
      toast.error('Vault password must be at least 4 characters');
      return;
    }
    if (vaultSetupPassword !== vaultSetupConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    setVaultVerifying(true);
    try {
      await api.post(`/customers/${customerId}/vault/set-password`, { password: vaultSetupPassword });
      toast.success('Vault password set! You can now unlock the vault.');
      setVaultNeedsSetup(false);
      setVaultSetupPassword('');
      setVaultSetupConfirm('');
      // Auto-unlock with the new password
      setVaultPassword(vaultSetupPassword);
      await api.post(`/customers/${customerId}/vault/verify`, { password: vaultSetupPassword });
      setVaultUnlocked(true);
      loadSecureNotes();
      loadCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set vault password');
    } finally {
      setVaultVerifying(false);
    }
  };

  const loadSecureNotes = async () => {
    try {
      const res = await api.get(`/customers/${customerId}/secure-notes`);
      setSecureNotes(res.data);
    } catch { setSecureNotes([]); }
  };

  const loadCredentials = async () => {
    try {
      const res = await api.get(`/customers/${customerId}/credentials`);
      setCredentials(res.data);
    } catch { setCredentials([]); }
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editingNoteId) {
        await api.put(`/customers/${customerId}/secure-notes/${editingNoteId}`, noteForm);
        toast.success('Note updated');
      } else {
        await api.post(`/customers/${customerId}/secure-notes`, noteForm);
        toast.success('Note created');
      }
      setNoteForm({ title: '', content: '' });
      setEditingNoteId(null);
      setShowNoteForm(false);
      loadSecureNotes();
    } catch { toast.error('Failed to save note'); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/customers/${customerId}/secure-notes/${noteId}`);
      toast.success('Note deleted');
      loadSecureNotes();
    } catch { toast.error('Failed to delete note'); }
  };

  const handleSaveCred = async () => {
    if (!credForm.title.trim()) { toast.error('Title is required'); return; }
    if (!editingCredId && !credForm.password) { toast.error('Password is required'); return; }
    try {
      if (editingCredId) {
        const payload = { ...credForm };
        if (!payload.password) delete payload.password;
        await api.put(`/customers/${customerId}/credentials/${editingCredId}`, payload);
        toast.success('Credential updated');
      } else {
        await api.post(`/customers/${customerId}/credentials`, credForm);
        toast.success('Credential created');
      }
      setCredForm({ title: '', username: '', password: '', url: '', notes: '', credential_type: 'generic' });
      setEditingCredId(null);
      setShowCredForm(false);
      loadCredentials();
    } catch { toast.error('Failed to save credential'); }
  };

  const handleDeleteCred = async (credId) => {
    try {
      await api.delete(`/customers/${customerId}/credentials/${credId}`);
      toast.success('Credential deleted');
      loadCredentials();
    } catch { toast.error('Failed to delete credential'); }
  };

  const handleRevealPassword = async (credId) => {
    if (revealedPasswords[credId]) {
      setRevealedPasswords(prev => { const n = { ...prev }; delete n[credId]; return n; });
      return;
    }
    setRevealingCredId(credId);
  };

  const confirmReveal = async () => {
    try {
      const res = await api.post(`/customers/${customerId}/credentials/${revealingCredId}/reveal`, { password: revealPassword });
      setRevealedPasswords(prev => ({ ...prev, [revealingCredId]: res.data.password }));
      setRevealingCredId(null);
      setRevealPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const credTypeIcon = (type) => {
    const icons = { server: Server, web: Globe, database: Database, vpn: Shield, email: Mail };
    const Icon = icons[type] || Key;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="co-page co-scope space-y-4 sm:space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-button"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3">
              <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <span className="line-clamp-1">{customer.company_name}</span>
            </h1>
            <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
              <Badge className={getStatusColor(customer.status)}>{customer.status}</Badge>
              {customer.contact_name && (
                <span className="text-xs sm:text-sm text-gray-600">Contact: {customer.contact_name}</span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setEditForm({
              company_name: customer.company_name || '',
              email: customer.email || '',
              phone: customer.phone || '',
              contact_name: customer.contact_name || '',
              status: customer.status || 'active',
              address: customer.address || '',
              city: customer.city || '',
              state: customer.state || '',
              zip_code: customer.zip_code || '',
              notes: customer.notes || '',
              domain: customer.domain || '',
            });
            setEditDialogOpen(true);
          }}
          data-testid="edit-customer-btn"
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Customer
        </Button>
        <Button
          variant="outline"
          onClick={() => setImageSearchOpen(true)}
          data-testid="customer-image-search-btn"
        >
          <Images className="h-4 w-4 mr-2" />
          Search Images
        </Button>
      </div>

      <ImageSearchDialog
        open={imageSearchOpen}
        onOpenChange={setImageSearchOpen}
        customerId={customerId}
        customerName={customer.company_name}
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/assets?customer_id=${customerId}`)}
          data-testid="stat-total-assets"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Total Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{assets.length}</div>
            <p className="text-xs text-gray-500 mt-1">
              {assets.filter(a => a.status === 'active').length} active
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/tickets?customer_id=${customerId}`)}
          data-testid="stat-open-tickets"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Ticket className="h-4 w-4" />
              Open Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
            </div>
            <p className="text-xs text-gray-500 mt-1">{tickets.length} total</p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/invoices?customer_id=${customerId}`)}
          data-testid="stat-invoices"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{invoices.length}</div>
            <p className="text-xs text-gray-500 mt-1">
              ${invoices.reduce((sum, inv) => sum + (inv.total || 0), 0).toFixed(2)} total
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/invoices?customer_id=${customerId}&status=sent`)}
          data-testid="stat-unpaid"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Unpaid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              ${invoices.filter(i => i.status !== 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0).toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {invoices.filter(i => i.status !== 'paid').length} invoices
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs — immediately below stats (UX3.1) */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <TabsList className="inline-flex w-max md:w-auto" data-testid="customer-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets ({tickets.length})</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">Credits</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({(customer.contacts || []).length})</TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal">Portal</TabsTrigger>
          <TabsTrigger value="secure-notes" data-testid="tab-secure-notes">
            <Lock className="h-3.5 w-3.5 mr-1" />Notes
          </TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">
            <Key className="h-3.5 w-3.5 mr-1" />Vault
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview">
      {/* Useful links / What's next — read-only navigation reusing existing tabs & loaded data */}
      <Card className="mb-4" data-testid="customer-useful-links">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            Useful links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => handleTabChange('tickets')} data-testid="link-tickets" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5">
              <Ticket className="h-3.5 w-3.5" /> Tickets ({tickets.length})
            </button>
            <button type="button" onClick={() => handleTabChange('assets')} data-testid="link-assets" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" /> Assets ({assets.length})
            </button>
            <button type="button" onClick={() => handleTabChange('invoices')} data-testid="link-invoices" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Invoices ({invoices.length})
            </button>
            <button type="button" onClick={() => handleTabChange('contacts')} data-testid="link-contacts" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Contacts ({(customer.contacts || []).length})
            </button>
            {customer.email && (
              <a href={`mailto:${customer.email}`} data-testid="link-email" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5 no-underline text-blue-600">
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            )}
            {customer.phone && (
              <a href={`tel:${customer.phone}`} data-testid="link-phone" className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5 no-underline text-blue-600">
                <Phone className="h-3.5 w-3.5" /> Call
              </a>
            )}
          </div>
        </CardContent>
      </Card>
      {/* Customer Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {customer.email && (
              <div>
                <Label className="text-xs text-gray-500">Email</Label>
                <p className="text-sm font-medium mt-1 flex items-center gap-2">
                  <Mail className="h-3 w-3 text-gray-400" />
                  {customer.email}
                </p>
              </div>
            )}
            {customer.phone && (
              <div>
                <Label className="text-xs text-gray-500">Phone</Label>
                <p className="text-sm font-medium mt-1 flex items-center gap-2">
                  <Phone className="h-3 w-3 text-gray-400" />
                  {customer.phone}
                </p>
              </div>
            )}
            {(customer.city || customer.state) && (
              <div>
                <Label className="text-xs text-gray-500">Location</Label>
                <p className="text-sm font-medium mt-1 flex items-center gap-2">
                  <MapPin className="h-3 w-3 text-gray-400" />
                  {customer.city}, {customer.state} {customer.zip_code}
                </p>
              </div>
            )}
            <div>
              <Label className="text-xs text-gray-500">Customer Since</Label>
              <p className="text-sm font-medium mt-1 flex items-center gap-2">
                <Calendar className="h-3 w-3 text-gray-400" />
                {formatInCST(customer.created_at, 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          {customer.address && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-xs text-gray-500">Address</Label>
              <p className="text-sm mt-1">{customer.address}</p>
              {(customer.city || customer.state || customer.zip_code) && (
                <p className="text-sm text-gray-600">{[customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')}</p>
              )}
              <div className="flex gap-2 mt-2">
                {(customer.address || customer.company_name) && (
                  <a
                    href={customer.directions_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(customer.address || customer.company_name)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 flex items-center gap-1 no-underline"
                    data-testid="map-me-btn"
                  >
                    <Globe className="h-3 w-3" /> Get Directions
                  </a>
                )}
                {(customer.google_maps_url || customer.address) && (
                  <a
                    href={customer.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address || customer.company_name)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 flex items-center gap-1 no-underline"
                  >
                    <ExternalLink className="h-3 w-3" /> View on Map
                  </a>
                )}
              </div>
              {customer.google_rating && (
                <p className="text-xs text-gray-500 mt-1">Google Rating: {'★'.repeat(Math.round(customer.google_rating))} {customer.google_rating}/5 ({customer.google_review_count || 0} reviews)</p>
              )}
            </div>
          )}
          {customer.notes && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-xs text-gray-500">Notes</Label>
              <p className="text-sm text-gray-600 mt-1">{customer.notes}</p>
            </div>
          )}
          {customer.description && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-xs text-gray-500">About</Label>
              <p className="text-sm text-gray-600 mt-1">{customer.description}</p>
            </div>
          )}

          {/* Company Details */}
          {(customer.website || customer.industry || customer.year_founded || customer.employee_count || customer.business_hours) && (
            <div className="mt-4 pt-4 border-t space-y-1">
              <Label className="text-xs text-gray-500">Company Details</Label>
              {customer.website && <p className="text-sm"><a href={customer.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><Globe className="h-3 w-3" /> {customer.website}</a></p>}
              {customer.industry && <p className="text-sm text-gray-600">Industry: {customer.industry}</p>}
              {customer.year_founded && <p className="text-sm text-gray-600">Founded: {customer.year_founded}</p>}
              {customer.employee_count && <p className="text-sm text-gray-600">Employees: {customer.employee_count}</p>}
              {customer.annual_revenue && <p className="text-sm text-gray-600">Revenue: {customer.annual_revenue}</p>}
              {customer.business_hours && <p className="text-sm text-gray-600">Hours: {customer.business_hours}</p>}
            </div>
          )}

          {/* Additional Contact */}
          {(customer.toll_free_phone || customer.fax) && (
            <div className="mt-4 pt-4 border-t space-y-1">
              <Label className="text-xs text-gray-500">Additional Contact</Label>
              {customer.toll_free_phone && <p className="text-sm text-gray-600">Toll-Free: {customer.toll_free_phone}</p>}
              {customer.fax && <p className="text-sm text-gray-600">Fax: {customer.fax}</p>}
            </div>
          )}

          {/* Social Media Links */}
          {(customer.linkedin_url || customer.facebook_url || customer.twitter_url || customer.instagram_url || customer.youtube_url || customer.google_reviews_url || customer.yelp_url) && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-xs text-gray-500">Social Media & Reviews</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {customer.linkedin_url && <a href={customer.linkedin_url} target="_blank" rel="noreferrer" className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> LinkedIn</a>}
                {customer.facebook_url && <a href={customer.facebook_url} target="_blank" rel="noreferrer" className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Facebook</a>}
                {customer.twitter_url && <a href={customer.twitter_url} target="_blank" rel="noreferrer" className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Twitter/X</a>}
                {customer.instagram_url && <a href={customer.instagram_url} target="_blank" rel="noreferrer" className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded hover:bg-purple-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Instagram</a>}
                {customer.youtube_url && <a href={customer.youtube_url} target="_blank" rel="noreferrer" className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> YouTube</a>}
                {customer.google_reviews_url && <a href={customer.google_reviews_url} target="_blank" rel="noreferrer" className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded hover:bg-green-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Google Reviews</a>}
                {customer.yelp_url && <a href={customer.yelp_url} target="_blank" rel="noreferrer" className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Yelp</a>}
              </div>
            </div>
          )}

          {/* Web Enrichment */}
          <div className="mt-4 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={handleEnrichFromWeb} disabled={enriching} data-testid="enrich-web-btn">
              {enriching ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
              {enriching ? 'Searching...' : 'Lookup Company Info'}
            </Button>
            {customer.social_links && Object.keys(customer.social_links).length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {Object.entries(customer.social_links).map(([platform, url]) => (
                  <a key={platform} href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> {platform}
                  </a>
                ))}
              </div>
            )}
            {customer.website && (
              <div className="mt-2">
                <a href={customer.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                  <Globe className="h-3 w-3" /> {customer.website}
                </a>
              </div>
            )}
          </div>

          {/* Enrichment Results */}
          {enrichResult && (
            <div className="mt-3 border rounded-lg p-3 bg-blue-50 space-y-2" data-testid="enrich-results">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-blue-800">Found Information</Label>
                <button className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setEnrichResult(null)}>Dismiss</button>
              </div>
              {[
                { key: 'website', label: 'Website' },
                { key: 'address', label: 'Address' },
                { key: 'phone', label: 'Phone' },
                { key: 'email', label: 'Email' },
                { key: 'description', label: 'About', maxLen: 150 },
                { key: 'industry', label: 'Industry' },
                { key: 'year_founded', label: 'Founded' },
                { key: 'employee_count', label: 'Employees' },
                { key: 'business_hours', label: 'Hours' },
                { key: 'fax', label: 'Fax' },
              ].map(({ key, label, maxLen }) => enrichResult[key] ? (
                <div key={key} className="flex items-start justify-between text-xs gap-2">
                  <span className="flex-1"><strong>{label}:</strong> {maxLen ? enrichResult[key].substring(0, maxLen) : enrichResult[key]}</span>
                </div>
              ) : null)}
              {enrichResult.google_rating && (
                <div className="text-xs"><strong>Google Rating:</strong> {'★'.repeat(Math.round(enrichResult.google_rating))} {enrichResult.google_rating}/5 ({enrichResult.google_review_count || 0} reviews)</div>
              )}
              {enrichResult.directions_url && (
                <a href={enrichResult.directions_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Globe className="h-3 w-3" /> Open in Google Maps</a>
              )}
              {Object.keys(enrichResult.social || {}).length > 0 && (
                <div className="text-xs">
                  <strong>Social Media:</strong>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {Object.entries(enrichResult.social).map(([p, url]) => (
                      <a key={p} href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline bg-white px-1.5 py-0.5 rounded border text-[11px]">{p}</a>
                    ))}
                  </div>
                </div>
              )}
              <Button size="sm" className="w-full mt-1 h-7 text-xs" onClick={() => handleApplyEnrichment({
                ...enrichResult, overwrite: false,
              })} data-testid="apply-all-enrichment-btn">
                Save All Found Info to Company Profile
              </Button>
            </div>
          )}

          {/* Payment Reminders Toggle */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between" data-testid="customer-reminders-section">
            <div>
              <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Payment Reminders
              </p>
              <p className="text-xs text-gray-500">Automatic email reminders for unpaid invoices</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const newVal = !customer.reminders_disabled;
                  await api.put(`/customers/${customer.id}`, { reminders_disabled: newVal });
                  setCustomer(prev => ({ ...prev, reminders_disabled: newVal }));
                  toast.success(newVal ? 'Payment reminders disabled' : 'Payment reminders enabled');
                } catch { toast.error('Failed to update'); }
              }}
              className={`relative w-10 h-5 rounded-full transition-colors ${customer.reminders_disabled ? 'bg-gray-300' : 'bg-emerald-500'}`}
              data-testid="customer-reminders-toggle"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${customer.reminders_disabled ? 'translate-x-0.5' : 'translate-x-5'}`} />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Contacts Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-neutral-500" />
              Contacts ({(customer.contacts || []).length})
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              data-testid="add-contact-btn"
              onClick={() => {
                setContactForm({ name: '', email: '', phone: '', title: '' });
                setEditingContact(null);
                setAddContactOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Contact
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add/Edit Contact Form */}
          {(addContactOpen || editingContact) && (
            <div className="mb-4 p-4 border rounded-lg bg-gray-50" data-testid="contact-form">
              <p className="text-sm font-medium mb-3">{editingContact ? 'Edit Contact' : 'New Contact'}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Input
                  placeholder="Name *"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  data-testid="contact-name-input"
                />
                <Input
                  placeholder="Email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email-input"
                />
                <Input
                  placeholder="Phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  data-testid="contact-phone-input"
                />
                <Input
                  placeholder="Job Title"
                  value={contactForm.title}
                  onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                  data-testid="contact-title-input"
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setAddContactOpen(false); setEditingContact(null); }}
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  data-testid="save-contact-btn"
                  onClick={editingContact ? handleUpdateContact : handleAddContact}
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> {editingContact ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          )}

          {(!customer.contacts || customer.contacts.length === 0) && !addContactOpen ? (
            <div className="text-center py-6 text-gray-400">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No contacts yet. Add employees or company contacts here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(customer.contacts || []).map((contact) => (
                <div key={contact.id || contact.email} className="p-3 border rounded-lg bg-neutral-50 group relative">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{contact.name || 'Unknown'}</p>
                      {contact.title && (
                        <p className="text-xs text-neutral-500 mt-0.5">{contact.title}</p>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        data-testid={`edit-contact-${contact.id}`}
                        onClick={() => {
                          setContactForm({
                            name: contact.name || '',
                            email: contact.email || '',
                            phone: contact.phone || '',
                            title: contact.title || '',
                          });
                          setEditingContact(contact);
                          setAddContactOpen(false);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-red-500 hover:text-red-700"
                        data-testid={`delete-contact-${contact.id}`}
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {contact.email && (
                    <p className="text-xs text-neutral-600 flex items-center gap-1 mt-1.5">
                      <Mail className="h-3 w-3" /> {contact.email}
                    </p>
                  )}
                  {contact.phone && (
                    <p className="text-xs text-neutral-600 flex items-center gap-1 mt-0.5">
                      <Phone className="h-3 w-3" /> {contact.phone}
                    </p>
                  )}
                  {contact.source && (
                    <Badge variant="outline" className="text-[10px] mt-1.5">{contact.source}</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="contacts">
          {/* Contacts moved here from overview (UX3.1) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contacts ({(customer.contacts || []).length})</CardTitle>
                <Button size="sm" variant="outline" data-testid="contacts-tab-add-btn" onClick={() => { setAddContactOpen(true); setEditingContact(null); setContactForm({ name: '', email: '', phone: '', title: '' }); }}>
                  <Plus className="h-4 w-4 mr-1" /> Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add/Edit Contact Form (now rendered on the Contacts tab too) */}
              {(addContactOpen || editingContact) && (
                <div className="mb-4 p-4 border rounded-lg bg-gray-50" data-testid="contacts-tab-contact-form">
                  <p className="text-sm font-medium mb-3">{editingContact ? 'Edit Contact' : 'New Contact'}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      placeholder="Name *"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      data-testid="contacts-tab-contact-name-input"
                    />
                    <Input
                      placeholder="Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      data-testid="contacts-tab-contact-email-input"
                    />
                    <Input
                      placeholder="Phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      data-testid="contacts-tab-contact-phone-input"
                    />
                    <Input
                      placeholder="Job Title"
                      value={contactForm.title}
                      onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                      data-testid="contacts-tab-contact-title-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button size="sm" variant="ghost" onClick={() => { setAddContactOpen(false); setEditingContact(null); }}>
                      <X className="h-3.5 w-3.5 mr-1" /> Cancel
                    </Button>
                    <Button size="sm" data-testid="contacts-tab-save-contact-btn" onClick={editingContact ? handleUpdateContact : handleAddContact}>
                      <Save className="h-3.5 w-3.5 mr-1" /> {editingContact ? 'Update' : 'Add'}
                    </Button>
                  </div>
                </div>
              )}

              {(customer.contacts || []).length === 0 && !addContactOpen && !editingContact ? (
                <p className="text-sm text-gray-500 py-4">No contacts yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(customer.contacts || []).map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-3 group relative">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm">{contact.name}</p>
                          {contact.title && <p className="text-xs text-neutral-500 mt-0.5">{contact.title}</p>}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-cyan-400"
                            aria-label={`Edit contact ${contact.name}`}
                            title={`Edit contact ${contact.name}`}
                            data-testid={`contacts-tab-edit-contact-${contact.id}`}
                            onClick={() => {
                              setContactForm({
                                name: contact.name || '',
                                email: contact.email || '',
                                phone: contact.phone || '',
                                title: contact.title || '',
                              });
                              setEditingContact(contact);
                              setAddContactOpen(false);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-red-500 hover:text-red-700 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-red-400"
                            aria-label={`Delete contact ${contact.name}`}
                            title={`Delete contact ${contact.name}`}
                            data-testid={`contacts-tab-delete-contact-${contact.id}`}
                            onClick={() => handleDeleteContact(contact.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {contact.email && <p className="text-xs text-gray-600 flex items-center gap-1 mt-1"><Mail className="h-3 w-3" /> {contact.email}</p>}
                      {contact.phone && <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {contact.phone}</p>}
                      {contact.source && <Badge variant="outline" className="text-[10px] mt-1.5">{contact.source}</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assets">
          {assets.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No assets for this customer
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <RecordLink
                  key={asset.id}
                  to={recordHref('asset', asset.id)}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                  fallbackClassName="block rounded-lg"
                  data-testid={`customer-asset-link-${asset.id}`}
                >
                  <Card className="hover:shadow-md transition-shadow h-full">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold">{asset.name}</h3>
                        <Badge className={getStatusColor(asset.status)}>{asset.status}</Badge>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>Type: {asset.asset_type}</p>
                        {asset.serial_number && <p className="font-mono text-xs">SN: {asset.serial_number}</p>}
                        {asset.manufacturer && <p>Make: {asset.manufacturer} {asset.model}</p>}
                      </div>
                      {asset.ninjarmm_device_id && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs mt-2">NinjaRMM</Badge>
                      )}
                    </CardContent>
                  </Card>
                </RecordLink>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tickets">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8 text-gray-500">
                <Ticket className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No tickets for this customer
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <RecordLink
                  key={ticket.id}
                  to={recordHref('ticket', ticket.id)}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                  fallbackClassName="block rounded-lg"
                  data-testid={`customer-ticket-link-${ticket.id}`}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-mono text-gray-500">{ticket.ticket_number}</span>
                            <Badge className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                            <Badge className={getStatusColor(ticket.status)}>{ticket.status}</Badge>
                          </div>
                          <h3 className="font-semibold">{ticket.title}</h3>
                          {ticket.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{htmlToText(ticket.description, { maxLength: 200 })}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Created {formatInCST(ticket.created_at, 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </RecordLink>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices">
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No invoices for this customer
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                {/* Mobile Card View */}
                <div className="md:hidden p-3 space-y-2" data-testid="customer-invoices-mobile">
                  {[...invoices].sort((a, b) => compareInvoices(a, b, invSort.field, invSort.order)).map((invoice) => (
                    <div
                      key={invoice.id}
                      className="border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow"
                      onClick={() => navigate(`/invoices/${invoice.id}?from=${buildReturnParam(location)}`)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold">{invoice.invoice_number}</span>
                            <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                          </div>
                        </div>
                        <span className="font-semibold whitespace-nowrap">${(invoice.total || 0).toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1.5">
                        {invoice.issue_date ? formatInCST(invoice.issue_date, 'MMM d, yyyy') : '-'} &middot; Due {invoice.due_date ? formatInCST(invoice.due_date, 'MMM d, yyyy') : '-'}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop Table View */}
                <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {[['invoice_number','Invoice #'],['issue_date','Issue Date'],['due_date','Due Date'],['total','Amount'],['status','Status']].map(([f,label]) => (
                        <TableHead key={f} role="button" tabIndex={0}
                          aria-sort={invSort.field === f ? (invSort.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className="cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                          onClick={() => toggleInvSort(f)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleInvSort(f); } }}
                          data-testid={`cust-inv-sort-${f}`}>
                          <div className="flex items-center gap-1">{label}
                            {invSort.field !== f ? <ArrowUpDown className="h-3 w-3 text-gray-300" />
                              : invSort.order === 'asc' ? <ArrowUp className="h-3 w-3 text-blue-600" /> : <ArrowDown className="h-3 w-3 text-blue-600" />}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...invoices].sort((a, b) => compareInvoices(a, b, invSort.field, invSort.order)).map((invoice) => (
                      <TableRow key={invoice.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-800/40" onClick={() => navigate(`/invoices/${invoice.id}?from=${buildReturnParam(location)}`)}>
                        <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-sm">
                          {invoice.issue_date ? formatInCST(invoice.issue_date, 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {invoice.due_date ? formatInCST(invoice.due_date, 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold">
                          ${(invoice.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Credits Tab */}
        <TabsContent value="credits">
          <CustomerCredits
            customerId={customer.id}
            customerName={customer.company_name}
            invoices={invoices}
          />
        </TabsContent>

        {/* Portal Access Tab */}
        <TabsContent value="portal">
          <PortalAccessManager customerId={customer.id} />
        </TabsContent>

        {/* Secure Notes Tab */}
        <TabsContent value="secure-notes" data-testid="secure-notes-tab">
          {!vaultUnlocked ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Lock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                {vaultNeedsSetup ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Set Vault Password</h3>
                    <p className="text-sm text-gray-500 mb-4">SSO users need to set a separate vault password for secure access</p>
                    <div className="max-w-xs mx-auto space-y-2">
                      <Input
                        type="password"
                        placeholder="New vault password"
                        value={vaultSetupPassword}
                        onChange={(e) => setVaultSetupPassword(e.target.value)}
                        data-testid="vault-setup-password"
                      />
                      <Input
                        type="password"
                        placeholder="Confirm vault password"
                        value={vaultSetupConfirm}
                        onChange={(e) => setVaultSetupConfirm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSetVaultPassword()}
                        data-testid="vault-setup-confirm"
                      />
                      <Button onClick={handleSetVaultPassword} disabled={vaultVerifying || !vaultSetupPassword} className="w-full" data-testid="vault-setup-btn">
                        {vaultVerifying ? 'Setting...' : 'Set Password & Unlock'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Admin Verification Required</h3>
                    <p className="text-sm text-gray-500 mb-4">Enter your password to access secure notes</p>
                    <div className="flex items-center gap-2 max-w-xs mx-auto">
                      <Input
                        type="password"
                        placeholder="Admin password"
                        value={vaultPassword}
                        onChange={(e) => setVaultPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUnlockVault()}
                        data-testid="vault-password-input"
                      />
                      <Button onClick={handleUnlockVault} disabled={vaultVerifying || !vaultPassword} data-testid="vault-unlock-btn">
                        {vaultVerifying ? 'Verifying...' : 'Unlock'}
                      </Button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setVaultNeedsSetup(true)}
                      className="text-xs text-blue-600 hover:underline mt-3"
                      data-testid="vault-reset-link"
                    >
                      Set or reset vault password
                    </button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">Secure Notes ({secureNotes.length})</h3>
                <Button size="sm" onClick={() => { setShowNoteForm(true); setEditingNoteId(null); setNoteForm({ title: '', content: '' }); }} data-testid="add-note-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Note
                </Button>
              </div>
              {showNoteForm && (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <Input placeholder="Note title" value={noteForm.title} onChange={(e) => setNoteForm(p => ({ ...p, title: e.target.value }))} data-testid="note-title-input" />
                    <Textarea placeholder="Note content..." rows={4} value={noteForm.content} onChange={(e) => setNoteForm(p => ({ ...p, content: e.target.value }))} data-testid="note-content-input" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveNote} data-testid="save-note-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowNoteForm(false); setEditingNoteId(null); }}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {secureNotes.map(note => (
                <Card key={note.id} data-testid={`note-${note.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{note.title}</h4>
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{note.content}</p>
                        <p className="text-xs text-gray-400 mt-2">By {note.created_by} &middot; {note.created_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingNoteId(note.id); setNoteForm({ title: note.title, content: note.content }); setShowNoteForm(true); }}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteNote(note.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {secureNotes.length === 0 && !showNoteForm && (
                <Card><CardContent className="py-8 text-center text-gray-500 text-sm">No secure notes yet</CardContent></Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Credentials Vault Tab */}
        <TabsContent value="credentials" data-testid="credentials-tab">
          {!vaultUnlocked ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Key className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                {vaultNeedsSetup ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Set Vault Password</h3>
                    <p className="text-sm text-gray-500 mb-4">SSO users need to set a separate vault password for secure access</p>
                    <div className="max-w-xs mx-auto space-y-2">
                      <Input
                        type="password"
                        placeholder="New vault password"
                        value={vaultSetupPassword}
                        onChange={(e) => setVaultSetupPassword(e.target.value)}
                        data-testid="vault-setup-password-cred"
                      />
                      <Input
                        type="password"
                        placeholder="Confirm vault password"
                        value={vaultSetupConfirm}
                        onChange={(e) => setVaultSetupConfirm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSetVaultPassword()}
                        data-testid="vault-setup-confirm-cred"
                      />
                      <Button onClick={handleSetVaultPassword} disabled={vaultVerifying || !vaultSetupPassword} className="w-full" data-testid="vault-setup-btn-cred">
                        {vaultVerifying ? 'Setting...' : 'Set Password & Unlock'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Admin Verification Required</h3>
                    <p className="text-sm text-gray-500 mb-4">Enter your password to access credentials vault</p>
                    <div className="flex items-center gap-2 max-w-xs mx-auto">
                      <Input
                        type="password"
                        placeholder="Admin password"
                        value={vaultPassword}
                        onChange={(e) => setVaultPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUnlockVault()}
                        data-testid="vault-password-input-cred"
                      />
                      <Button onClick={handleUnlockVault} disabled={vaultVerifying || !vaultPassword} data-testid="vault-unlock-btn-cred">
                        {vaultVerifying ? 'Verifying...' : 'Unlock'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">Credentials ({credentials.length})</h3>
                <Button size="sm" onClick={() => { setShowCredForm(true); setEditingCredId(null); setCredForm({ title: '', username: '', password: '', url: '', notes: '', credential_type: 'generic' }); }} data-testid="add-cred-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Credential
                </Button>
              </div>
              {showCredForm && (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Title (e.g., Main Server)" value={credForm.title} onChange={(e) => setCredForm(p => ({ ...p, title: e.target.value }))} data-testid="cred-title-input" />
                      <Select value={credForm.credential_type} onValueChange={(v) => setCredForm(p => ({ ...p, credential_type: v }))}>
                        <SelectTrigger data-testid="cred-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generic">Generic</SelectItem>
                          <SelectItem value="server">Server</SelectItem>
                          <SelectItem value="web">Web</SelectItem>
                          <SelectItem value="database">Database</SelectItem>
                          <SelectItem value="vpn">VPN</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Username" value={credForm.username} onChange={(e) => setCredForm(p => ({ ...p, username: e.target.value }))} data-testid="cred-username-input" />
                      <Input type="password" placeholder={editingCredId ? "Leave blank to keep current" : "Password"} value={credForm.password} onChange={(e) => setCredForm(p => ({ ...p, password: e.target.value }))} data-testid="cred-password-input" />
                    </div>
                    <Input placeholder="URL (optional)" value={credForm.url} onChange={(e) => setCredForm(p => ({ ...p, url: e.target.value }))} data-testid="cred-url-input" />
                    <Textarea placeholder="Notes (optional)" rows={2} value={credForm.notes} onChange={(e) => setCredForm(p => ({ ...p, notes: e.target.value }))} data-testid="cred-notes-input" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveCred} data-testid="save-cred-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowCredForm(false); setEditingCredId(null); }}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {credentials.map(cred => (
                <Card key={cred.id} data-testid={`cred-${cred.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {credTypeIcon(cred.credential_type)}
                          <h4 className="font-semibold text-sm">{cred.title}</h4>
                          <Badge variant="outline" className="text-xs">{cred.credential_type}</Badge>
                        </div>
                        <div className="mt-2 space-y-1 text-sm">
                          {cred.username && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 w-20">Username:</span>
                              <span className="font-mono text-xs bg-gray-50 px-2 py-0.5 rounded">{cred.username}</span>
                              <button onClick={() => copyToClipboard(cred.username)} className="text-gray-400 hover:text-gray-600"><Copy className="h-3 w-3" /></button>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 w-20">Password:</span>
                            <span className="font-mono text-xs bg-gray-50 px-2 py-0.5 rounded">
                              {revealedPasswords[cred.id] || '••••••••'}
                            </span>
                            <button onClick={() => handleRevealPassword(cred.id)} className="text-gray-400 hover:text-gray-600" data-testid={`reveal-pwd-${cred.id}`}>
                              {revealedPasswords[cred.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                            {revealedPasswords[cred.id] && (
                              <button onClick={() => copyToClipboard(revealedPasswords[cred.id])} className="text-gray-400 hover:text-gray-600"><Copy className="h-3 w-3" /></button>
                            )}
                          </div>
                          {cred.url && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 w-20">URL:</span>
                              <span className="font-mono text-xs text-blue-600">{cred.url}</span>
                              <button onClick={() => copyToClipboard(cred.url)} className="text-gray-400 hover:text-gray-600"><Copy className="h-3 w-3" /></button>
                            </div>
                          )}
                          {cred.notes && <p className="text-xs text-gray-500 mt-1">{cred.notes}</p>}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">By {cred.created_by} &middot; {cred.created_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingCredId(cred.id); setCredForm({ title: cred.title, username: cred.username, password: '', url: cred.url, notes: cred.notes, credential_type: cred.credential_type }); setShowCredForm(true); }}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteCred(cred.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {credentials.length === 0 && !showCredForm && (
                <Card><CardContent className="py-8 text-center text-gray-500 text-sm">No credentials stored yet</CardContent></Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Reveal Password Dialog */}
        <Dialog open={!!revealingCredId} onOpenChange={() => { setRevealingCredId(null); setRevealPassword(''); }}>
          <DialogContent className="co-scope max-w-sm">
            <DialogHeader>
              <DialogTitle>Verify Identity</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">Re-enter your admin password to reveal this credential.</p>
            <Input
              type="password"
              placeholder="Admin password"
              value={revealPassword}
              onChange={(e) => setRevealPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && confirmReveal()}
              data-testid="reveal-password-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setRevealingCredId(null); setRevealPassword(''); }}>Cancel</Button>
              <Button size="sm" onClick={confirmReveal} disabled={!revealPassword} data-testid="confirm-reveal-btn">Reveal</Button>
            </div>
          </DialogContent>
        </Dialog>
      </Tabs>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="co-scope max-w-2xl" data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const dataToSubmit = { ...editForm };
              if (!dataToSubmit.email) delete dataToSubmit.email;
              await api.put(`/customers/${customerId}`, dataToSubmit);
              toast.success('Customer updated successfully');
              setEditDialogOpen(false);
              loadCustomerDetails();
            } catch (error) {
              toast.error('Failed to update customer');
            }
          }} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} required data-testid="edit-company-name" />
              </div>
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Zip Code</Label>
                <Input value={editForm.zip_code} onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Domain</Label>
                <Input value={editForm.domain} onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })} placeholder="e.g. company.com" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Update Customer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
